/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { generateAPIKey, fetchAPIKey, revokeAPIKey, selectSettings } from '../../reducers/settings'
import { useSelector } from 'react-redux'
import { Button, Card, Flex, IconButton, Separator, Text } from '@radix-ui/themes'
import Select, { StylesConfig } from 'react-select'
import { CopyIcon, EyeNoneIcon, EyeOpenIcon } from '@radix-ui/react-icons'
import { formatUtcTimestamp } from '../../utils/dates'
import IntegrationModal from './IntegrationModal'
import toast from 'react-hot-toast'
import { selectUser } from '../../reducers/users'
import { RoleType } from '../../types/UserTypes'
import { getRoles, permitsRole } from '../../utils/roles'
import chroma from 'chroma-js'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'

function ApiSettings() {
  const { apiKey } = useSelector(selectSettings)
  const [showKey, setShowKey] = useState<boolean>(false)
  const [showRevokeModal, setShowRevokeModal] = useState<boolean>(false)
  const [screenToShow, setScreenToShow] = useState<string>('REVOKE')
  const [expiry, setExpiry] = useState({ value: 'ONE_YEAR', label: '1 Year' })
  const di = useAppDispatch()
  const { session, darkModeEnabled } = useSelector(selectUser)

  const options = [
    { value: 'SIX_MONTHS', label: '6 Months' },
    { value: 'ONE_YEAR', label: '1 Year' },
    { value: 'NEVER', label: 'Never' },
  ]

  const roles = getRoles(session)
  const rolePermitsAPIKeyPolicy = permitsRole(roles, [RoleType.PLATFORM_MGMT])

  useEffect(() => {
    if (rolePermitsAPIKeyPolicy) {
      di(fetchAPIKey())
    }

    if (roles && !rolePermitsAPIKeyPolicy) {
      window.location.href = '/forbidden'
    }
  }, [session])

  const revokeKey = () => {
    di(revokeAPIKey())
    toast.success('API Key successfully revoked', DEFAULT_TOAST_CONFIG)
  }

  const generateKey = () => {
    if (apiKey) {
      setScreenToShow('REGENERATE')
      setShowRevokeModal(true)
    } else {
      di(generateAPIKey(expiry.value))
      toast.success('API Key successfully generated', DEFAULT_TOAST_CONFIG)
    }
  }

  const regenerateKey = () => {
    di(generateAPIKey(expiry.value))
    toast.success('API Key successfully regenerated', DEFAULT_TOAST_CONFIG)
  }

  const combinedStyles: StylesConfig<any, true> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: darkModeEnabled ? '#333' : '#fff',
      borderColor: darkModeEnabled ? '#555' : '#ccc',
      color: darkModeEnabled ? '#fff' : '#000',
      height: '38px',
      minHeight: '38px',
      boxShadow: state.isFocused ? (darkModeEnabled ? '0 0 0 2px #6958ad' : '0 0 0 2px #aa99ec') : undefined,
      '&:hover': {
        borderColor: state.isFocused ? 'transparent' : '#aa99ec',
        cursor: 'pointer',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: '0px',
      paddingBottom: '0px',
      fontSize: '14px',
    }),
    placeholder: (base) => ({
      ...base,
      color: darkModeEnabled ? '#aaa' : '#555',
      height: '30px',
      minHeight: '30px',
      paddingTop: '0px',
    }),
    singleValue: (base) => ({
      ...base,
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    input: (base) => ({
      ...base,
      height: '36px',
      minHeight: '36px',
      marginTop: '0px',
      marginBottom: '0px',
      paddingTop: '0px',
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkModeEnabled ? '#333' : '#fff',
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? darkModeEnabled
          ? '#444'
          : '#ddd'
        : isFocused
          ? darkModeEnabled
            ? '#555'
            : '#eee'
          : 'transparent',
      color: darkModeEnabled ? '#fff' : '#000',
    }),
    multiValue: (base, { data }) => ({
      ...base,
      backgroundColor: chroma(data.bgColor).css(),
      marginTop: '-4px',
    }),
    multiValueLabel: (base, { data }) => ({
      ...base,
      color: data.color,
    }),
    multiValueRemove: (base, { data }) => ({
      ...base,
      color: data.color,
      ':hover': {
        backgroundColor: data.color,
        color: 'white',
      },
    }),
  }

  return (
    <>
      <div className="w-full h-[calc(100vh_-_80px)] overflow-auto mt-6">
        {/* API Key settings */}
        {rolePermitsAPIKeyPolicy && (
          <Flex direction="column" gap="3" maxWidth="100%" className="z-40">
            <Card variant="surface">
              <Text as="div" size="2" weight="bold">
                API Settings
              </Text>
              <Separator my="3" size="4" className="dark:bg-gray-500" />
              {apiKey && (
                <div>
                  <Text as="div" size="1" className="mt-2 text-secondaryText">
                    API Key
                  </Text>
                  <div className="w-full flex justify-between space-x-4">
                    <div className="h-[38px] flex mt-1 border-mainBorder border rounded px-2 py-2.5 justify-between text-sm w-2/3">
                      {showKey ? apiKey.token : '*********************'}
                      <div className="flex">
                        <IconButton size="2" variant="ghost" className="mr-1" onClick={() => setShowKey(!showKey)}>
                          {showKey ? <EyeNoneIcon height="16" width="16" /> : <EyeOpenIcon height="16" width="16" />}
                        </IconButton>
                        <IconButton
                          size="2"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(apiKey.token)
                            toast.success('API Key copied to clipboard', DEFAULT_TOAST_CONFIG)
                          }}
                        >
                          <CopyIcon height="16" width="16" />
                        </IconButton>
                      </div>
                    </div>
                    <Button
                      variant="solid"
                      className="h-[38px] w-1/3 bg-ErrorRed cursor-pointer mt-1 float-right"
                      onClick={() => {
                        setScreenToShow('REVOKE')
                        setShowRevokeModal(true)
                      }}
                    >
                      Revoke API Key
                    </Button>
                  </div>
                  <Text as="div" size="1" className="mt-1 text-secondaryText">
                    {apiKey.expiresAt
                      ? 'Expires ' + formatUtcTimestamp(apiKey.expiresAt, false, true)
                      : 'API Key set to not expire'}
                  </Text>
                </div>
              )}
              <Text as="div" size="1" className="mt-2 text-secondaryText">
                Expiry
              </Text>
              <div className="flex mt-1">
                <Select
                  defaultValue={options.find((option) => option.value === 'ONE_YEAR')}
                  onChange={(e: any) => setExpiry(e)}
                  options={options}
                  className={'w-full mr-4 z-50  not-form-styled'}
                  menuPortalTarget={document.body}
                  styles={{ ...combinedStyles }}
                />
                <Button
                  variant="solid"
                  className="h-[38px] w-1/3 bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple text-white cursor-pointer"
                  onClick={() => generateKey()}
                >
                  {apiKey ? 'Re-g' : 'G'}enerate API Key
                </Button>
              </div>
              {expiry.value !== 'ONE_YEAR' && (
                <Text as="div" size="2" className="mt-4 text-secondaryText">
                  Consider choosing 1-year token to rotate credentials periodically
                </Text>
              )}
            </Card>
          </Flex>
        )}
      </div>
      {showRevokeModal && (
        <IntegrationModal
          setShowIntegrationModal={setShowRevokeModal}
          screenToShow={screenToShow}
          revokeKey={revokeKey}
          regenerateKey={regenerateKey}
        />
      )}
    </>
  )
}

export default ApiSettings
